import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/i18n";
import { CheckCircle2, Loader2, MapPin, Calendar, Car, Banknote, FileText, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingConfirmation() {
  const { id } = useParams();
  const { t } = useLang();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bookings/${id}`)
      .then((res) => setBooking(res.data))
      .catch(() => setError(true));
  }, [id]);

  const method = booking?.payment_method || "stripe";
  const header = {
    especes: {
      icon: <Banknote className="w-10 h-10 text-[#c9a24d]" />,
      title: t("conf.cashTitle"),
      note: booking ? t("conf.cashNote", { price: booking.price_eur.toFixed(2) }) : "",
    },
    devis: {
      icon: <FileText className="w-10 h-10 text-[#c9a24d]" />,
      title: t("conf.devisTitle"),
      note: t("conf.devisNote"),
    },
    stripe: {
      icon: booking?.payment_status === "paid"
        ? <CheckCircle2 className="w-10 h-10 text-[#34d399]" />
        : <CreditCard className="w-10 h-10 text-[#c9a24d]" />,
      title: booking?.payment_status === "paid" ? t("conf.paidTitle") : t("conf.pendingTitle"),
      note: booking?.payment_status === "paid" ? t("conf.paidNote") : t("conf.pendingNote"),
    },
  }[method];

  return (
    <div className="min-h-screen bg-[#060709] text-[#f4f4f7] flex items-center justify-center px-6" data-testid="booking-confirmation-page">
      <div className="gold-card rounded-2xl p-8 md:p-12 max-w-xl w-full">
        {!booking && !error && (
          <div className="flex flex-col items-center gap-4 py-8" data-testid="confirmation-loading">
            <Loader2 className="w-10 h-10 text-[#c9a24d] animate-spin" />
          </div>
        )}
        {error && (
          <div className="text-center" data-testid="confirmation-error">
            <h1 className="font-disp text-2xl text-white mb-3">{t("conf.notFound")}</h1>
            <Link to="/" className="font-geom px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] bg-[#12151e] text-[#c9a24d] rounded-full border border-[#c9a24d]/40 inline-block" data-testid="back-home-link">
              {t("conf.back")}
            </Link>
          </div>
        )}
        {booking && (
          <div data-testid="confirmation-content">
            <div className="flex items-center gap-3 mb-6">
              {header.icon}
              <div>
                <h1 className="font-disp text-2xl md:text-3xl text-white" data-testid="confirmation-title">{header.title}</h1>
                <p className="font-mono text-xs text-[#c9a24d] uppercase tracking-widest" data-testid="confirmation-reference">{t("conf.ref")} {booking.reference}</p>
              </div>
            </div>
            <p className="text-sm text-[#9ea8b6] mb-5" data-testid="confirmation-note">{header.note}</p>
            <div className="space-y-3 text-sm text-[#c6cedb]">
              <div className="flex items-start gap-3" data-testid="conf-route">
                <MapPin className="w-4 h-4 text-[#c9a24d] mt-0.5 shrink-0" />
                <span>{booking.pickup} → {booking.destination}</span>
              </div>
              <div className="flex items-center gap-3" data-testid="conf-datetime">
                <Calendar className="w-4 h-4 text-[#c9a24d] shrink-0" />
                <span>{booking.date} {t("conf.at")} {booking.time} · {booking.distance_km} km (~{booking.duration_min} min)</span>
              </div>
              <div className="flex items-center gap-3" data-testid="conf-vehicle">
                <Car className="w-4 h-4 text-[#c9a24d] shrink-0" />
                <span>{booking.vehicle_label}</span>
              </div>
              <div className="flex items-center gap-3" data-testid="conf-price">
                <Banknote className="w-4 h-4 text-[#c9a24d] shrink-0" />
                <span><strong className="text-[#c9a24d]">{booking.price_eur.toFixed(2)} €</strong>
                  {method === "especes" && ` ${t("conf.cashSuffix")}`}
                  {method === "devis" && ` ${t("conf.devisSuffix")}`}
                  {method === "stripe" && booking.payment_status === "paid" && ` ${t("conf.paidSuffix")}`}
                </span>
              </div>
            </div>
            <p className="mt-6 text-xs text-[#8c98a9]">
              {t("conf.thanks", { name: booking.name })}
            </p>
            <Link to="/" data-testid="back-home-link"
              className="mt-6 inline-block font-geom px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] bg-gradient-to-r from-[#c9a24d] via-[#d9b86c] to-[#a17c2e] text-[#060709] rounded-full">
              {t("conf.back")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
